/**
 * Voice type content configuration
 * Contains display text, descriptions, and examples for each voice type
 * Separate from voice-types.ts to keep enum definitions clean
 */

export interface VoiceTypeContent {
  title: string;
  description: string;
  vibe: string;
  examples: string;
  promise: string;
  explanation?: string;
}

export const VOICE_TYPE_CONTENT = {
  MALE: {
    BASS: {
      title: "Bass",
      description:
        "Your voice lives in the low floors — deep, powerful, and warm. Think 'movie trailer energy' 😄",
      vibe: "Rich lows • Calm authority • Big resonance",
      examples:
        "Often described as bass or bass-baritone: Avi Kaplan (Pentatonix), Barry White, Johnny Cash",
      promise:
        "Don't worry about high notes — we'll transpose songs into your sweet spot and build your range safely.",
    },

    BARITONE: {
      title: "Baritone",
      description:
        "The ultimate all-rounder. Your voice sits right in the 'main character' zone — strong, smooth, and super flexible.",
      vibe: "Warm mids • Easy power • Great for pop/rock",
      examples:
        "Often described as baritone: Frank Sinatra, Elvis Presley, John Legend, David Bowie",
      promise:
        "Most songs can fit you easily — we'll adjust the key so it feels natural and confident.",
    },

    TENOR: {
      title: "Tenor",
      description:
        "Bright, high, and shining — your voice is built for big hooks and soaring notes. Hello, chorus hero 🔥",
      vibe: "Brilliant highs • Strong melody lines • Big 'ring'",
      examples:
        "Often described as tenor: Freddie Mercury, Bruno Mars, Sam Smith",
      promise:
        "We'll fine-tune song keys so your high notes feel effortless (and not like a struggle).",
    },
    ALTO: {
      title: "High Tenor / Alto",
      description:
        "Your voice sits unusually high for a male singer — bright, light, and super agile. You're basically living in the 'upper floors' 🕊️",
      vibe: "High range • Clear tone • Fast & flexible",
      explanation:
        "You might be between Tenor and Alto — totally normal. Some voices naturally sit there, especially in pop, R&B, and lighter styles.",
      examples:
        "High male voices you'll recognize: Justin Timberlake, Mika, Prince",
      promise:
        "We'll transpose every song to your perfect key so your high notes feel smooth (not forced).",
    },

    MEZZO_SOPRANO: {
      title: "Very High Male (Tenor–Mezzo Range)",
      description:
        "You've got a rare high male voice with serious sparkle. Think clean top notes and smooth melodies that float ✨",
      vibe: "Bright highs • Smooth head voice • Great for modern pop",
      explanation:
        "This can happen if your speaking voice is lighter, or if you naturally prefer singing higher. Nothing is 'wrong' — it's a cool advantage.",
      examples:
        "Similar vibes (high male voices): Sam Smith, Bruno Mars, The Weeknd",
      promise:
        "We'll match songs to your range and build power + control so your highs stay effortless.",
    },

    SOPRANO: {
      title: "Soprano (Rare Male)",
      description:
        "Okay wow — this is *super rare* for a male singer. Your voice naturally sits very high, with a clear, bright 'ring' 🌟",
      vibe: "Very high range • Crystal clarity • Unique signature sound",
      explanation:
        "This can happen with an exceptionally high natural range, or if you're using a strong head voice/falsetto. Either way — it's a special instrument.",
      examples:
        "High male voices / soprano-like range: Mitch Grassi (Pentatonix), Dimash (extreme range), some countertenor singers",
      promise:
        "No worries — we'll adjust every song so it fits you perfectly, and we'll train your voice safely and comfortably.",
    },
  },

  FEMALE: {
    ALTO: {
      title: "Alto (Contralto)",
      description:
        "Your voice is deep, velvety, and rare — the 'cool dark chocolate' of vocals 😌",
      vibe: "Low warmth • Soulful tone • Powerful storytelling",
      examples:
        "Often described as contralto/low alto: Nina Simone, Tracy Chapman, Karen Carpenter",
      promise:
        "No pressure to sing super high — we'll adjust songs to your perfect range and strengthen your lows.",
    },

    MEZZO_SOPRANO: {
      title: "Mezzo-Soprano",
      description:
        "The golden middle: warm lows + bright highs. You can sound soft and powerful — sometimes in the same line ✨",
      vibe: "Rich mid-range • Flexible • Great emotional color",
      examples: "Often described as mezzo: Adele, Beyoncé, Lady Gaga",
      promise:
        "We'll tune songs to sit right in your comfort zone, then expand your range step-by-step.",
    },

    SOPRANO: {
      title: "Soprano",
      description:
        "Bright, clear, and sparkling — your voice loves the upper notes. It's the 'sunlight' voice ☀️",
      vibe: "Shining highs • Clear tone • Big top notes",
      examples:
        "Often described as soprano: Ariana Grande, Mariah Carey, Whitney Houston",
      promise:
        "We'll tailor songs so your highs stay clean (not strained), and your lower notes stay strong too.",
    },
    TENOR: {
      title: "Low Mezzo / Tenor Range",
      description:
        "Your voice sits lower than average for a female singer — warm, strong, and super grounded. It has that 'cool confidence' sound 😌",
      vibe: "Strong chest voice • Warm lows • Powerful mids",
      explanation:
        "You might be between Mezzo-Soprano and Tenor. That's completely normal — some voices naturally live lower.",
      examples:
        "Similar low-female vibes: Adele (lower mezzo feel), Annie Lennox, Sade",
      promise:
        "We'll transpose every song into your sweet spot, so it feels natural and powerful — not forced.",
    },

    BARITONE: {
      title: "Deep Female Voice (Baritone Range)",
      description:
        "Ooh — you've got a *deep* voice with rich color. This is the 'velvet espresso' type ☕️🎶",
      vibe: "Dark tone • Rich resonance • Big presence",
      explanation:
        "This is rarer for female singers, but it's a real thing — especially in soulful styles or when your chest voice is strong.",
      examples:
        "Deep female tone inspiration: Tracy Chapman, Nina Simone, Cher",
      promise:
        "We'll adapt songs so the notes sit comfortably in your range, and help you build control and smooth transitions.",
    },

    BASS: {
      title: "Ultra-Deep Female Voice (Rare)",
      description:
        "Wow — this is *very rare* for a female voice. Your sound is deep, dramatic, and instantly recognizable 🖤",
      vibe: "Very low range • Massive warmth • Unique signature",
      explanation:
        "Some voices naturally have a very low foundation — it can be your natural instrument or your strongest comfort zone.",
      examples:
        "Closest vibe examples: Nina Simone (very low warmth), some contralto singers, and deep-voiced storytellers in soul/jazz",
      promise:
        "No stress — we'll tune every song for you automatically and focus on making your low notes smooth, strong, and healthy.",
    },    
  },
} as const;

export type VoiceTypeContentMap = typeof VOICE_TYPE_CONTENT;
